export const typographyStyleKeys = [
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'p1',
  'p2',
  'p3',
  'label',
  'button',
  'small',
] as const;

export type TypographyStyleKey = (typeof typographyStyleKeys)[number];
export type TypographyViewport = 'desktop' | 'mobile';
export type TypographySemanticTag = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p';

export interface WebEditorTypographySize {
  desktop?: number;
  mobile?: number;
}

export type WebEditorTypographyScale = Partial<Record<TypographyStyleKey, WebEditorTypographySize>>;

export interface WebEditorTypographyFrame {
  typographyStyle?: TypographyStyleKey;
  fontSizeDesktop?: number;
  fontSizeMobile?: number;
}

export interface TypographyStyleDefinition {
  key: TypographyStyleKey;
  label: string;
  desktop: number;
  mobile: number;
  semanticTag?: TypographySemanticTag;
}

export const typographyStyleDefinitions: readonly TypographyStyleDefinition[] = [
  { key: 'h1', label: 'H1', desktop: 64, mobile: 42, semanticTag: 'h1' },
  { key: 'h2', label: 'H2', desktop: 48, mobile: 34, semanticTag: 'h2' },
  { key: 'h3', label: 'H3', desktop: 36, mobile: 28, semanticTag: 'h3' },
  { key: 'h4', label: 'H4', desktop: 28, mobile: 23, semanticTag: 'h4' },
  { key: 'h5', label: 'H5', desktop: 22, mobile: 19, semanticTag: 'h5' },
  { key: 'h6', label: 'H6', desktop: 18, mobile: 17, semanticTag: 'h6' },
  { key: 'p1', label: 'P1', desktop: 20, mobile: 18, semanticTag: 'p' },
  { key: 'p2', label: 'P2', desktop: 16, mobile: 16, semanticTag: 'p' },
  { key: 'p3', label: 'P3', desktop: 14, mobile: 14, semanticTag: 'p' },
  { key: 'label', label: 'Label', desktop: 13, mobile: 13 },
  { key: 'button', label: 'Button', desktop: 15, mobile: 15 },
  { key: 'small', label: 'Small', desktop: 12, mobile: 12 },
] as const;

const definitionsByKey = new Map(typographyStyleDefinitions.map(definition => [definition.key, definition]));

export const isTypographyStyleKey = (value: unknown): value is TypographyStyleKey =>
  typeof value === 'string' && typographyStyleKeys.includes(value as TypographyStyleKey);

export const clampTypographySize = (value: number): number => Math.min(240, Math.max(8, Math.round(value)));

const normalizedSize = (value: unknown): number | undefined =>
  typeof value === 'number' && Number.isFinite(value) ? clampTypographySize(value) : undefined;

export const automaticMobileFontSize = (desktop: number, style?: TypographyStyleKey): number => {
  const safeDesktop = clampTypographySize(desktop);
  const definition = style ? definitionsByKey.get(style) : undefined;
  if (definition) return clampTypographySize(safeDesktop * (definition.mobile / definition.desktop));
  return clampTypographySize(Math.min(safeDesktop, 48, Math.max(14, 14 + (safeDesktop - 14) * 0.58)));
};

export const normalizeTypographyScale = (incoming: unknown): WebEditorTypographyScale => {
  if (typeof incoming !== 'object' || incoming === null) return {};
  const record = incoming as Record<string, unknown>;
  const scale: WebEditorTypographyScale = {};
  for (const key of typographyStyleKeys) {
    const entry = record[key];
    if (typeof entry !== 'object' || entry === null) continue;
    const sizes = entry as Record<string, unknown>;
    const desktop = normalizedSize(sizes.desktop);
    const mobile = normalizedSize(sizes.mobile);
    if (desktop !== undefined || mobile !== undefined) {
      scale[key] = {
        ...(desktop !== undefined ? { desktop } : {}),
        ...(mobile !== undefined ? { mobile } : {}),
      };
    }
  }
  return scale;
};

export const typographyStyleSizes = (
  scale: WebEditorTypographyScale | undefined,
  style: TypographyStyleKey,
): { desktop: number; mobile: number; mobileIsAutomatic: boolean } => {
  const definition = definitionsByKey.get(style)!;
  const configured = scale?.[style];
  const desktop = configured?.desktop ?? definition.desktop;
  const mobileIsAutomatic = configured?.mobile === undefined;
  const mobile = configured?.mobile ?? automaticMobileFontSize(desktop, style);
  return { desktop, mobile, mobileIsAutomatic };
};

export const resolveTypographySizes = (
  scale: WebEditorTypographyScale | undefined,
  frame: WebEditorTypographyFrame | undefined,
): { desktop?: number; mobile?: number; mobileIsAutomatic: boolean } | null => {
  if (!frame) return null;
  const hasLocalDesktop = frame.fontSizeDesktop !== undefined;
  const hasLocalMobile = frame.fontSizeMobile !== undefined;
  if (!hasLocalDesktop && !hasLocalMobile && !frame.typographyStyle) return null;

  const global = frame.typographyStyle ? typographyStyleSizes(scale, frame.typographyStyle) : undefined;
  const desktop = frame.fontSizeDesktop ?? global?.desktop;
  const mobileIsAutomatic = !hasLocalMobile;
  const mobile =
    frame.fontSizeMobile ??
    (desktop === undefined
      ? undefined
      : hasLocalDesktop
        ? automaticMobileFontSize(desktop, frame.typographyStyle)
        : (global?.mobile ?? automaticMobileFontSize(desktop)));
  return {
    ...(desktop !== undefined ? { desktop: clampTypographySize(desktop) } : {}),
    ...(mobile !== undefined ? { mobile: clampTypographySize(mobile) } : {}),
    mobileIsAutomatic,
  };
};

export const typographySemanticTag = (style: TypographyStyleKey | undefined) =>
  style ? definitionsByKey.get(style)?.semanticTag : undefined;

export const resolveTypographySemanticTag = (
  frame: WebEditorTypographyFrame | undefined,
  fallbackTag: TypographySemanticTag,
): TypographySemanticTag => typographySemanticTag(frame?.typographyStyle) ?? fallbackTag;
